import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  IoCheckmarkCircle,
  IoClose,
  IoAlertCircle,
  IoInformationCircle,
  IoWarning,
  IoTrashOutline,
  IoHelpCircleOutline,
} from 'react-icons/io5';
import { useTheme } from './ThemeContext';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  let isLightMode = false;
  try {
    const themeContext = useTheme();
    isLightMode =
      themeContext?.isLightMode ??
      (typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light');
  } catch (e) {
    isLightMode =
      typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light';
  }

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3500, extras = {}) => {
    const id = Date.now() + Math.random().toString(36).substring(2, 9);
    const newToast = { id, message, type, duration, ...extras };

    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const confirm = useCallback((message, onConfirm, options = {}) => {
    // Remove existing confirm toasts to avoid stacking duplicates
    setToasts((prev) => prev.filter((t) => t.type !== 'confirm'));

    return addToast(message, 'confirm', 0, {
      onConfirm,
      title: options.title,
      confirmText: options.confirmText || 'Delete',
      cancelText: options.cancelText || 'Cancel',
      confirmVariant: options.confirmVariant || 'danger',
      onCancel: options.onCancel,
    });
  }, [addToast]);

  const toast = useCallback(
    Object.assign(
      (message, type, duration) => addToast(message, type, duration),
      {
        success: (msg, duration) => addToast(msg, 'success', duration),
        error: (msg, duration) => addToast(msg, 'error', duration),
        info: (msg, duration) => addToast(msg, 'info', duration),
        warning: (msg, duration) => addToast(msg, 'warning', duration),
        confirm,
      }
    ),
    [addToast, confirm]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className="fixed top-5 right-5 z-[99999] flex flex-col gap-3 max-w-[420px] w-full pointer-events-none px-4 sm:px-0"
        aria-live="polite"
      >
        {toasts.map((item) => (
          <ToastItem
            key={item.id}
            toast={item}
            isLightMode={isLightMode}
            onClose={() => {
              removeToast(item.id);
              if (item.type === 'confirm' && item.onCancel) item.onCancel();
            }}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, isLightMode: propIsLightMode, onClose }) => {
  let isLightMode = propIsLightMode;
  try {
    const themeContext = useTheme();
    if (themeContext && typeof themeContext.isLightMode === 'boolean') {
      isLightMode = themeContext.isLightMode;
    }
  } catch (e) {
    if (typeof document !== 'undefined') {
      isLightMode = document.documentElement.dataset.theme === 'light';
    }
  }

  const { type, message, onConfirm, title, confirmText, cancelText, confirmVariant, duration } = toast;

  // Light mode: white background + vibrant sky blue border & accents (very visible)
  // Dark mode: dark background + crisp white text + electric blue border & accents
  const styles = isLightMode
    ? {
        card:
          type === 'error'
            ? 'bg-white border-2 border-rose-400 border-l-[6px] border-l-rose-500 shadow-xl shadow-rose-500/15'
            : type === 'warning'
            ? 'bg-white border-2 border-amber-400 border-l-[6px] border-l-amber-500 shadow-xl shadow-amber-500/15'
            : 'bg-white border-2 border-sky-400 border-l-[6px] border-l-sky-500 shadow-xl shadow-sky-500/20',
        text: 'text-slate-900',
        subtext:
          type === 'error'
            ? 'text-rose-600'
            : type === 'warning'
            ? 'text-amber-600'
            : 'text-sky-700',
        badge:
          type === 'error'
            ? 'bg-rose-50 text-rose-700 border-rose-200'
            : type === 'warning'
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-sky-50 text-sky-700 border-sky-200',
        closeBtn: 'text-slate-400 hover:text-sky-700 hover:bg-sky-50',
        cancelBtn: 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300',
        confirmBtnDanger: 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white shadow-sm',
        confirmBtnPrimary: 'bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white shadow-sm',
        separator: 'border-slate-200',
        progressBar:
          type === 'error'
            ? 'bg-rose-500'
            : type === 'warning'
            ? 'bg-amber-500'
            : 'bg-sky-500',
        icons: {
          success: <IoCheckmarkCircle className="text-sky-500 shrink-0 text-2xl" />,
          error: <IoAlertCircle className="text-rose-500 shrink-0 text-2xl" />,
          warning: <IoWarning className="text-amber-500 shrink-0 text-2xl" />,
          info: <IoInformationCircle className="text-sky-500 shrink-0 text-2xl" />,
          confirm:
            confirmVariant === 'danger' ? (
              <IoTrashOutline className="text-rose-500 shrink-0 text-2xl" />
            ) : (
              <IoHelpCircleOutline className="text-sky-600 shrink-0 text-2xl" />
            ),
        },
      }
    : {
        card:
          type === 'error'
            ? 'bg-[#0c1322] border-2 border-rose-500 border-l-[6px] border-l-rose-400 shadow-2xl shadow-rose-950/80 backdrop-blur-xl'
            : type === 'warning'
            ? 'bg-[#0c1322] border-2 border-amber-500 border-l-[6px] border-l-amber-400 shadow-2xl shadow-amber-950/80 backdrop-blur-xl'
            : 'bg-[#0c1322] border-2 border-blue-500 border-l-[6px] border-l-blue-400 shadow-2xl shadow-blue-950/80 backdrop-blur-xl',
        text: 'text-white',
        subtext:
          type === 'error'
            ? 'text-rose-400'
            : type === 'warning'
            ? 'text-amber-400'
            : 'text-blue-300',
        badge:
          type === 'error'
            ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
            : type === 'warning'
            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
            : 'bg-blue-500/20 text-blue-300 border-blue-500/40',
        closeBtn: 'text-white/50 hover:text-white hover:bg-white/10',
        cancelBtn: 'bg-white/10 hover:bg-white/20 text-white/90 border border-white/15',
        confirmBtnDanger: 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-sm',
        confirmBtnPrimary: 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white shadow-sm',
        separator: 'border-white/10',
        progressBar:
          type === 'error'
            ? 'bg-rose-500'
            : type === 'warning'
            ? 'bg-amber-400'
            : 'bg-blue-400',
        icons: {
          success: <IoCheckmarkCircle className="text-blue-400 shrink-0 text-2xl" />,
          error: <IoAlertCircle className="text-rose-400 shrink-0 text-2xl" />,
          warning: <IoWarning className="text-amber-400 shrink-0 text-2xl" />,
          info: <IoInformationCircle className="text-blue-400 shrink-0 text-2xl" />,
          confirm:
            confirmVariant === 'danger' ? (
              <IoTrashOutline className="text-rose-400 shrink-0 text-2xl" />
            ) : (
              <IoHelpCircleOutline className="text-blue-400 shrink-0 text-2xl" />
            ),
        },
      };

  const icon = styles.icons[type] || styles.icons.info;

  if (type === 'confirm') {
    return (
      <div
        className={`pointer-events-auto relative overflow-hidden flex flex-col gap-3 p-4 rounded-xl toast-card-enter transition-all duration-200 ${styles.card}`}
        role="alertdialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          {icon}
          <div className="flex-1 min-w-0">
            {title && (
              <div className={`text-[11px] font-bold uppercase tracking-wider mb-0.5 ${styles.subtext}`}>
                {title}
              </div>
            )}
            <div className={`text-xs font-semibold leading-relaxed break-words ${styles.text}`}>
              {message}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`transition-colors p-1 rounded-md shrink-0 ${styles.closeBtn}`}
            aria-label="Close"
          >
            <IoClose size={16} />
          </button>
        </div>

        <div className={`flex items-center justify-end gap-2 pt-2.5 border-t ${styles.separator}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${styles.cancelBtn}`}
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              if (onConfirm) onConfirm();
            }}
            className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition ${
              confirmVariant === 'danger' ? styles.confirmBtnDanger : styles.confirmBtnPrimary
            }`}
          >
            {confirmText || 'Delete'}
          </button>
        </div>
      </div>
    );
  }

  const defaultTag =
    type === 'success' ? 'Success' : type === 'error' ? 'Error' : type === 'warning' ? 'Notice' : null;

  return (
    <div
      className={`pointer-events-auto relative overflow-hidden flex items-start gap-3 p-3.5 rounded-xl toast-card-enter transition-all duration-200 ${styles.card}`}
      role="alert"
    >
      {icon}
      <div className="flex-1 min-w-0 pt-0.5">
        {defaultTag && (
          <div className="mb-0.5">
            <span
              className={`inline-block px-1.5 py-0.2 text-[10px] font-bold uppercase tracking-wider rounded border ${styles.badge}`}
            >
              {defaultTag}
            </span>
          </div>
        )}
        <div className={`text-xs font-semibold leading-relaxed break-words ${styles.text}`}>
          {message}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className={`transition-colors p-1 rounded-md shrink-0 ${styles.closeBtn}`}
        aria-label="Close"
      >
        <IoClose size={16} />
      </button>

      {duration > 0 && (
        <div
          className={`absolute bottom-0 left-0 h-[3px] toast-progress-bar ${styles.progressBar}`}
          style={{ animationDuration: `${duration}ms` }}
        />
      )}
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    return {
      success: (msg) => alert(msg),
      error: (msg) => alert(msg),
      info: (msg) => alert(msg),
      warning: (msg) => alert(msg),
      confirm: (msg, onConfirm) => {
        if (window.confirm(msg)) onConfirm();
      },
    };
  }
  return context;
};

export default ToastContext;
